import {equals} from 'ramda';

import {omneticApi} from '@dms/api/base';

import {
  GetSalePresetSettingsApiArg,
  GetSalePresetSettingsApiResponse,
  DisablePublishCebiaSmartCodeUrlApiArg,
  DisablePublishCebiaSmartCodeUrlApiResponse,
  CreateSalePresetSettingsApiArg,
  CreateSalePresetSettingsApiResponse,
  DeleteSalePresetSettingsApiArg,
  DeleteSalePresetSettingsApiResponse,
  UpdateSalePresetSettingsApiArg,
  UpdateSalePresetSettingsApiResponse,
  EditGeneralSettingsApiArg,
  EditGeneralSettingsApiResponse,
  EditPlatformSettingsApiArg,
  EditPlatformSettingsApiResponse,
  EditWindshieldSettingsApiArg,
  EditWindshieldSettingsApiResponse,
  EnablePublishCebiaSmartCodeUrlApiArg,
  EnablePublishCebiaSmartCodeUrlApiResponse,
  GetCebiaSmartCodeUrlConfigApiArg,
  GetCebiaSmartCodeUrlConfigApiResponse,
  GetBusinessCaseDocumentsSettingsApiResponse,
  PutBusinessCaseDocumentsSettingsApiArg,
  PutBusinessCaseDocumentsSettingsApiResponse,
  GetGeneralSettingsApiArg,
  GetGeneralSettingsApiResponse,
  GetGenerationsApiArg,
  GetGenerationsApiResponse,
  GetPlatformSettingsApiArg,
  GetPlatformSettingsApiResponse,
  GetSalesPlatformsApiArg,
  GetSalesPlatformsApiRes,
  GetVersionsApiArg,
  GetVersionsApiResponse,
  GetWindshieldSettingsApiArg,
  GetWindshieldSettingsApiResponse,
  UpdateVehicleIsInSaleApiArg,
  UpdateVehicleIsInSaleApiRes,
  UpdateVehicleOnAllPlatformsApiArg,
  UpdateVehicleOnAllPlatformsApiResponse,
  UpdateVehicleOnPlatformApiArg,
  UpdateVehicleOnPlatformApiResponse,
  CreatePurchaseBusinessCaseApiArg,
  CreatePurchaseBusinessCaseApiResponse,
  CreateSaleBusinessCaseApiArg,
  CreateSaleBusinessCaseApiResponse,
  UpdateKeyLocationApiArg,
  UpdateKeyLocationApiResponse,
  GetDigitalCertificateThemesApiResponse,
  CreateSalePresetSettingsV2ApiArg,
  CreateSalePresetSettingsV2ApiResponse,
  DeleteSalePresetSettingsV2ApiArg,
  DeleteSalePresetSettingsV2ApiResponse,
  GetSalePresetSettingsListApiArg,
  GetSalePresetSettingsListApiResponse,
  GetSalePresetSettingsV2ApiArg,
  GetSalePresetSettingsV2ApiResponse,
  UpdateSalePresetSettingsV2ApiArg,
  UpdateSalePresetSettingsV2ApiResponse,
  SetSalesFinanceSettingsApiArg,
  SetSalesFinanceSettingsApiResponse,
  GetSalesFinanceSettingsApiResponse,
  CreateBusinessCaseActivityApiArg,
  CreateBusinessCaseActivityApiResponse,
  GetBusinessCaseActivityListApiArg,
  GetBusinessCaseActivityListApiResponse,
  CreateInterestActivityApiArg,
  CreateInterestActivityApiResponse,
  GetInterestActivityListApiArg,
  GetInterestActivityListApiResponse,
  PatchSalesActivityApiResponse,
  PatchSalesActivityApiArg,
  DeleteSalesActivityApiResponse,
  DeleteSalesActivityApiArg,
  GetActiveQuickPresetsApiResponse,
  GetActiveQuickPresetsApiArg,
  DisablePlatformForVehicleApiResponse,
  DisablePlatformForVehicleApiArg,
  EnablePlatformForVehicleApiResponse,
  EnablePlatformForVehicleApiArg,
  RepeatLastActionApiResponse,
  RepeatLastActionApiArg,
  GetWindshieldPaperTemplatesApiResponse,
  GetWindshieldPaperTemplatesApiArg,
  RenderWindshieldPaperDocumentApiResponse,
  RenderWindshieldPaperDocumentApiArg,
  GenerateVgpCertificateApiArg,
} from './types';

export const salesApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    updateVehicleIsInSale: build.mutation<UpdateVehicleIsInSaleApiRes, UpdateVehicleIsInSaleApiArg>(
      {
        query: ({vehicleId, publish, validateMandatoryFields}) => ({
          url: `/dms/v2/sales/vehicle/${vehicleId}/${publish ? 'publish' : 'unpublish'}`,
          method: 'PUT',
          params: {
            validateMandatoryFields,
          },
        }),
        invalidatesTags: ['AdGeneralSettings', 'AdPlatformSettings', 'AdWindshieldSettings'],
      }
    ),
    getSalesPlatforms: build.query<GetSalesPlatformsApiRes, GetSalesPlatformsApiArg>({
      query: ({vehicleId}) => ({
        url: `/dms/v2/sales/publishing/vehicle/${vehicleId}/advertisements`,
      }),
      providesTags: ['AdGeneralSettings'],
      // Invalidate tags if any platform status has changed
      async onQueryStarted(arg, {dispatch, getCacheEntry, queryFulfilled}) {
        try {
          const oldPlatforms = getCacheEntry().data;
          const {data: newPlatforms} = await queryFulfilled;

          if (Array.isArray(oldPlatforms) && Array.isArray(newPlatforms)) {
            const toStatusDict = (list: GetSalesPlatformsApiRes | undefined) =>
              Object.fromEntries((list ?? []).map((p) => [p.platform.code, p.status] as const));

            if (!equals(toStatusDict(oldPlatforms), toStatusDict(newPlatforms))) {
              dispatch(
                salesApi.util.invalidateTags(['AdPlatformSettings', 'AdWindshieldSettings'])
              );
            }
          }
        } catch {} // Query failed, no need to handle status comparison
      },
    }),
    updateVehicleOnAllPlatforms: build.mutation<
      UpdateVehicleOnAllPlatformsApiResponse,
      UpdateVehicleOnAllPlatformsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/updateOnAllPlatforms`,
        method: 'PUT',
      }),
    }),
    updateVehicleOnPlatform: build.mutation<
      UpdateVehicleOnPlatformApiResponse,
      UpdateVehicleOnPlatformApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/platform/${queryArg.platformCode}/update`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, {vehicleId}) => [
        {type: 'AdGeneralSettings', id: vehicleId},
        'AdPlatformSettings',
      ],
    }),
    editGeneralSettings: build.mutation<EditGeneralSettingsApiResponse, EditGeneralSettingsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}`,
        method: 'PATCH',
        body: queryArg.patchVehicleAdvertisingSettingsRequestBody,
      }),
      invalidatesTags: ['AdGeneralSettings', 'AdPlatformSettings', 'AdWindshieldSettings'],
    }),
    getGeneralSettings: build.query<GetGeneralSettingsApiResponse, GetGeneralSettingsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}`,
      }),
      providesTags: ['AdGeneralSettings'],
    }),
    editWindshieldSettings: build.mutation<
      EditWindshieldSettingsApiResponse,
      EditWindshieldSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/windshield`,
        method: 'PATCH',
        body: queryArg.patchVehicleWindshieldAdvertisingSettingsRequestBody,
      }),
      invalidatesTags: ['AdWindshieldSettings'],
    }),
    getWindshieldSettings: build.query<
      GetWindshieldSettingsApiResponse,
      GetWindshieldSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/windshield`,
      }),
      providesTags: ['AdWindshieldSettings'],
    }),
    editPlatformSettings: build.mutation<
      EditPlatformSettingsApiResponse,
      EditPlatformSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/platform/${queryArg.platformCode}`,
        method: 'PATCH',
        body: queryArg.vehiclePlatformAdvertisingSettingsRequestBody,
      }),
      invalidatesTags: ['AdPlatformSettings'],
    }),
    getPlatformSettings: build.query<GetPlatformSettingsApiResponse, GetPlatformSettingsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/platform/${queryArg.platformCode}`,
      }),
      providesTags: ['AdPlatformSettings'],
    }),
    getBusinessCaseDocumentsSettings: build.query<
      GetBusinessCaseDocumentsSettingsApiResponse,
      void
    >({
      query: () => ({
        url: `/dms/v1/settings/business-case/documents`,
      }),
      providesTags: ['BusinessCaseSettings'],
    }),
    putBusinessCaseDocumentsSettings: build.mutation<
      PutBusinessCaseDocumentsSettingsApiResponse,
      PutBusinessCaseDocumentsSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/settings/business-case/documents`,
        method: 'PUT',
        body: queryArg.businessCaseDocumentsSettingsRequestBody,
      }),
      invalidatesTags: ['BusinessCaseSettings'],
    }),
    getSalesFinanceSettings: build.query<GetSalesFinanceSettingsApiResponse, void>({
      query: () => ({
        url: `/dms/v1/settings/sales/finance`,
      }),
      providesTags: ['SalesFinanceSettings'],
    }),
    setSalesFinanceSettings: build.mutation<
      SetSalesFinanceSettingsApiResponse,
      SetSalesFinanceSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/settings/sales/finance`,
        method: 'PUT',
        body: queryArg.salesFinanceSettingsRequestBody,
      }),
      invalidatesTags: ['SalesFinanceSettings'],
    }),
    createPurchaseBusinessCase: build.mutation<
      CreatePurchaseBusinessCaseApiResponse,
      CreatePurchaseBusinessCaseApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/purchase`,
        method: 'POST',
        body: queryArg.createPurchaseBusinessCaseRequestBody,
      }),
      invalidatesTags: () => ['SaleVehicleActions'],
    }),
    createSaleBusinessCase: build.mutation<
      CreateSaleBusinessCaseApiResponse,
      CreateSaleBusinessCaseApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/sale`,
        method: 'POST',
        body: queryArg.createSaleBusinessCaseRequestBody,
      }),
      invalidatesTags: (_, __, arg) => [
        {
          type: 'VehicleLastActiveBC',
          id: arg.createSaleBusinessCaseRequestBody.vehicleToSaleId,
        },
        {
          type: 'BusinessCaseCountByVehicle',
          id: arg.createSaleBusinessCaseRequestBody.vehicleToSaleId,
        },
      ],
    }),
    getSalePresetSettings: build.query<
      GetSalePresetSettingsApiResponse,
      GetSalePresetSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/settings/sale-preset/${queryArg.salePresetId}`,
      }),
    }),
    getSalePresetSettingsList: build.query<
      GetSalePresetSettingsListApiResponse,
      GetSalePresetSettingsListApiArg
    >({
      query: () => ({
        url: `/dms/v1/settings/sale-preset`,
      }),
    }),
    createSalePresetSettings: build.mutation<
      CreateSalePresetSettingsApiResponse,
      CreateSalePresetSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/settings/sale-preset`,
        method: 'POST',
        body: queryArg.createSalePresetSettingsRequestBody,
      }),
    }),
    deleteSalePresetSettings: build.mutation<
      DeleteSalePresetSettingsApiResponse,
      DeleteSalePresetSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/settings/sale-preset/${queryArg.salePresetId}`,
        method: 'DELETE',
      }),
    }),
    updateSalePresetSettings: build.mutation<
      UpdateSalePresetSettingsApiResponse,
      UpdateSalePresetSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/settings/sale-preset/${queryArg.salePresetId}`,
        method: 'PUT',
        body: queryArg.updateSalePresetSettingsRequestBody,
      }),
    }),

    createSalePresetSettingsV2: build.mutation<
      CreateSalePresetSettingsV2ApiResponse,
      CreateSalePresetSettingsV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/settings/sale-preset`,
        method: 'POST',
        body: queryArg.createSalePresetSettingsV2RequestBody,
      }),
    }),
    deleteSalePresetSettingsV2: build.mutation<
      DeleteSalePresetSettingsV2ApiResponse,
      DeleteSalePresetSettingsV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/settings/sale-preset/${queryArg.salePresetId}`,
        method: 'DELETE',
        body: queryArg.deleteSalePresetSettingsV2RequestBody,
      }),
    }),
    getSalePresetSettingsV2: build.query<
      GetSalePresetSettingsV2ApiResponse,
      GetSalePresetSettingsV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/settings/sale-preset/${queryArg.salePresetId}`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'SalePreset', id: queryArg.salePresetId},
      ],
    }),
    updateSalePresetSettingsV2: build.mutation<
      UpdateSalePresetSettingsV2ApiResponse,
      UpdateSalePresetSettingsV2ApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/settings/sale-preset/${queryArg.salePresetId}`,
        method: 'PUT',
        body: queryArg.updateSalePresetSettingsV2RequestBody,
      }),
    }),
    updateKeyLocation: build.mutation<UpdateKeyLocationApiResponse, UpdateKeyLocationApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/keyLocation`,
        method: 'PATCH',
        body: queryArg.updateVehicleKeyLocationRequestBody,
      }),
      invalidatesTags: (_, __, arg) => [{type: 'Vehicle', id: arg.vehicleId}],
    }),
    disablePublishCebiaSmartCodeUrl: build.mutation<
      DisablePublishCebiaSmartCodeUrlApiResponse,
      DisablePublishCebiaSmartCodeUrlApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/disable-publish-cebia-smart-code-url`,
        method: 'PUT',
      }),
      async onQueryStarted({vehicleId}, {dispatch, queryFulfilled}) {
        // OPTIMISTIC RESPONSE
        const patchResult = dispatch(
          salesApi.util.updateQueryData(
            'getPublishCebiaSmartCodeUrlConfig',
            {vehicleId},
            (draft) => {
              draft.publishCebiaSmartCodeUrl = false;
            }
          )
        );
        await queryFulfilled.catch(patchResult.undo);
      },
      invalidatesTags: (result, error, queryArg) => [
        {type: 'CebiaSmartCodeUrlConfig', id: queryArg.vehicleId},
      ],
    }),
    enablePublishCebiaSmartCodeUrl: build.mutation<
      EnablePublishCebiaSmartCodeUrlApiResponse,
      EnablePublishCebiaSmartCodeUrlApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/enable-publish-cebia-smart-code-url`,
        method: 'PUT',
      }),
      async onQueryStarted({vehicleId}, {dispatch, queryFulfilled}) {
        // OPTIMISTIC RESPONSE
        const patchResult = dispatch(
          salesApi.util.updateQueryData(
            'getPublishCebiaSmartCodeUrlConfig',
            {vehicleId},
            (draft) => {
              draft.publishCebiaSmartCodeUrl = true;
            }
          )
        );
        await queryFulfilled.catch(patchResult.undo);
      },
      invalidatesTags: (result, error, queryArg) => [
        {type: 'CebiaSmartCodeUrlConfig', id: queryArg.vehicleId},
      ],
    }),
    getPublishCebiaSmartCodeUrlConfig: build.query<
      GetCebiaSmartCodeUrlConfigApiResponse,
      GetCebiaSmartCodeUrlConfigApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/cebia-smart-code-url-config`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'CebiaSmartCodeUrlConfig', id: queryArg.vehicleId},
      ],
    }),
    getGenerations: build.query<GetGenerationsApiResponse, GetGenerationsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/generations`,
      }),
    }),
    getVersions: build.query<GetVersionsApiResponse, GetVersionsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/versions`,
      }),
    }),
    getDigitalCertificateThemes: build.query<GetDigitalCertificateThemesApiResponse, void>({
      query: () => ({
        url: `/dms/v2/sales/publishing/digital-certificate-themes`,
      }),
    }),
    getActiveQuickPresets: build.query<
      GetActiveQuickPresetsApiResponse,
      GetActiveQuickPresetsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/active-quick-preset`,
      }),
    }),
    getBusinessCaseActivityList: build.query<
      GetBusinessCaseActivityListApiResponse,
      GetBusinessCaseActivityListApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/sales-activity`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'BusinessCaseActivity', id: queryArg.businessCaseId},
      ],
    }),
    createBusinessCaseActivity: build.mutation<
      CreateBusinessCaseActivityApiResponse,
      CreateBusinessCaseActivityApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/sales-activity`,
        method: 'POST',
        body: queryArg.createSalesActivityRequestBody,
      }),
      invalidatesTags: (_, __, arg) => [
        {type: 'BusinessCaseActivity', id: arg.businessCaseId},
        {type: 'BusinessCaseDetail', id: arg.businessCaseId},
      ],
    }),
    createInterestActivity: build.mutation<
      CreateInterestActivityApiResponse,
      CreateInterestActivityApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/sales-activity`,
        method: 'POST',
        body: queryArg.createSalesActivityRequestBody,
      }),
      invalidatesTags: (_, __, arg) => [
        {type: 'InterestActivity', id: arg.interestId},
        {type: 'Interest', id: arg.interestId},
      ],
    }),
    deleteSalesActivity: build.mutation<DeleteSalesActivityApiResponse, DeleteSalesActivityApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sales-activity/${queryArg.salesActivityId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => ['InterestActivity', 'BusinessCaseActivity'],
    }),
    patchSalesActivity: build.mutation<PatchSalesActivityApiResponse, PatchSalesActivityApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sales-activity/${queryArg.salesActivityId}`,
        method: 'PATCH',
        body: queryArg.patchSalesActivityRequestBody,
      }),
      invalidatesTags: () => ['InterestActivity', 'BusinessCaseActivity'],
    }),
    getInterestActivityList: build.query<
      GetInterestActivityListApiResponse,
      GetInterestActivityListApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest/${queryArg.interestId}/sales-activity`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'InterestActivity', id: queryArg.interestId},
      ],
    }),
    disablePlatformForVehicle: build.mutation<
      DisablePlatformForVehicleApiResponse,
      DisablePlatformForVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/platforms/${queryArg.platformCode}/disable`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, {vehicleId}) => [
        {type: 'AdGeneralSettings', id: vehicleId},
        'AdPlatformSettings',
      ],
    }),
    enablePlatformForVehicle: build.mutation<
      EnablePlatformForVehicleApiResponse,
      EnablePlatformForVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/platforms/${queryArg.platformCode}/enable`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, {vehicleId}) => [
        {type: 'AdGeneralSettings', id: vehicleId},
        'AdPlatformSettings',
      ],
    }),
    repeatLastAction: build.mutation<RepeatLastActionApiResponse, RepeatLastActionApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/sales/publishing/vehicle/${queryArg.vehicleId}/platform/${queryArg.platformCode}/repeat`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, {vehicleId}) => [
        {type: 'AdGeneralSettings', id: vehicleId},
        'AdPlatformSettings',
      ],
    }),
    getWindshieldPaperTemplates: build.query<
      GetWindshieldPaperTemplatesApiResponse,
      GetWindshieldPaperTemplatesApiArg
    >({
      query: () => ({
        url: `/dms/v1/sales/document/windshield-paper/templates`,
      }),
      providesTags: ['DocumentTemplates'],
    }),
    renderWindshieldPaperDocument: build.mutation<
      RenderWindshieldPaperDocumentApiResponse,
      RenderWindshieldPaperDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sales/document/windshield-paper`,
        method: 'PUT',
        body: queryArg.windshieldPaperRequestBody,
      }),
    }),
    generateVgpCertificate: build.mutation<void, GenerateVgpCertificateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/generate-vgp-certificate`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useUpdateVehicleIsInSaleMutation,
  useGetSalesPlatformsQuery,
  useLazyGetSalesPlatformsQuery,
  useUpdateVehicleOnAllPlatformsMutation,
  useUpdateVehicleOnPlatformMutation,
  useEditGeneralSettingsMutation,
  useGetGeneralSettingsQuery,
  useEditWindshieldSettingsMutation,
  useGetWindshieldSettingsQuery,
  useEditPlatformSettingsMutation,
  useGetPlatformSettingsQuery,
  useDisablePublishCebiaSmartCodeUrlMutation,
  useEnablePublishCebiaSmartCodeUrlMutation,
  useGetPublishCebiaSmartCodeUrlConfigQuery,
  useGetVersionsQuery,
  useCreateSalePresetSettingsMutation,
  useDeleteSalePresetSettingsMutation,
  useUpdateSalePresetSettingsMutation,
  useGetSalePresetSettingsQuery,
  useGetSalePresetSettingsListQuery,
  useLazyGetSalePresetSettingsListQuery,
  useCreateSalePresetSettingsV2Mutation,
  useDeleteSalePresetSettingsV2Mutation,
  useGetSalePresetSettingsV2Query,
  useLazyGetSalePresetSettingsV2Query,
  useUpdateSalePresetSettingsV2Mutation,
  useGetGenerationsQuery,
  useGetActiveQuickPresetsQuery,
  useGetDigitalCertificateThemesQuery,
  useGetBusinessCaseDocumentsSettingsQuery,
  useCreatePurchaseBusinessCaseMutation,
  useCreateSaleBusinessCaseMutation,
  useUpdateKeyLocationMutation,
  usePutBusinessCaseDocumentsSettingsMutation,
  useGetSalesFinanceSettingsQuery,
  useSetSalesFinanceSettingsMutation,
  useGetBusinessCaseActivityListQuery,
  useCreateBusinessCaseActivityMutation,
  useCreateInterestActivityMutation,
  useGetInterestActivityListQuery,
  useDeleteSalesActivityMutation,
  usePatchSalesActivityMutation,
  useDisablePlatformForVehicleMutation,
  useEnablePlatformForVehicleMutation,
  useRepeatLastActionMutation,
  useGetWindshieldPaperTemplatesQuery,
  useRenderWindshieldPaperDocumentMutation,
  useGenerateVgpCertificateMutation,
} = salesApi;
